import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { Link } from "react-router-dom";
import { Check, Crown } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Mascot from "@/components/Mascot";
import { makeApi, publicApi } from "@/lib/api";
import { isAndroidApp } from "@/lib/platform";

const PERKS = ["Submit your own quizzes & study sets", "No ads, ever", "Unlimited lives", "Keeps TP running for everyone"];

export default function Pricing() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [me, setMe] = useState(null);
  const [cfg, setCfg] = useState({ price: 2.99, paypal_enabled: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me").then((r) => setMe(r.data)).catch(() => {});
    publicApi.get("/billing/config").then((r) => setCfg(r.data)).catch(() => {});
  }, [api]);

  const checkout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel at the end of this billing period?")) return;
    try { await api.post("/billing/cancel"); toast.success("Subscription will end at period end"); setMe((m) => ({ ...m, cancel_at_period_end: true })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <Nav />
      <main className="max-w-4xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Become a TP Supporter</h1>
          <p className="mt-3 text-slate-700 text-lg">A tiny monthly roll that keeps the app running — and unlocks the good stuff.</p>
          <div className="mt-8 hidden md:block"><Mascot size={200} /></div>
        </div>
        <div className="sticker-card p-8" data-testid="pricing-card">
          <div className="flex items-center gap-2 font-black text-[var(--tp-success)]"><Crown strokeWidth={2.5} /> Supporter</div>
          <p className="mt-3"><span className="text-5xl font-black">${cfg.price}</span><span className="text-slate-500 font-bold"> / month</span></p>
          <ul className="mt-6 space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-2 font-bold">
                <span className="w-6 h-6 rounded-full bg-[var(--tp-success)] border-[2px] border-[#374151] flex items-center justify-center text-white"><Check size={14} strokeWidth={3} /></span>{p}
              </li>
            ))}
          </ul>
          {me?.subscribed ? (
            <div className="mt-8" data-testid="already-subscribed">
              <p className="font-black text-[var(--tp-success)]">You're a Supporter. Thank you!</p>
              {isAndroidApp() ? null : me.cancel_at_period_end ? (
                <p className="text-sm text-slate-500 mt-1">Ends at the close of this billing period.</p>
              ) : (
                <button onClick={cancel} data-testid="cancel-subscription" className="mt-3 text-sm font-bold underline text-slate-600">Cancel subscription</button>
              )}
            </div>
          ) : isAndroidApp() ? (
            <div className="mt-8">
              <p className="font-bold text-slate-700" data-testid="pricing-android-note">Supporter plans can't be bought in the Android app. If you're already a Supporter, your perks work here automatically.</p>
              <Link to="/play" data-testid="pricing-android-back" className="mt-4 inline-block px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151]">Back to playing</Link>
            </div>
          ) : (
            <div className="mt-8 space-y-3">
              <button onClick={checkout} disabled={busy} data-testid="stripe-checkout-btn"
                className="w-full py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none disabled:opacity-60">
                {busy ? "Redirecting…" : "Subscribe with card"}
              </button>
              <button disabled={!cfg.paypal_enabled} data-testid="paypal-checkout-btn"
                onClick={() => toast.info("PayPal checkout coming soon")}
                className="w-full py-3 rounded-full border-[3px] border-[#374151] bg-[#FFC439] text-[#003087] font-black shadow-[0_4px_0_0_#374151] disabled:opacity-50 disabled:cursor-not-allowed">
                Pay with PayPal {cfg.paypal_enabled ? "" : "(coming soon)"}
              </button>
              <p className="text-xs text-slate-500 text-center">Cancel anytime. Secure checkout by Stripe.</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
